import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { fetchData, postData } from '../../helpers/apiUtils';
import ImageDropZone from '../../components/ImageDropZone';
import {
  Abschnitt,
  BEGRIFF,
  Feld,
  GefahrBestaetigung,
  NurAdmin,
  useFeldpruefung,
  type FeldBefund,
} from '../../components/marketingForm';
import TinyMCEEditor, { type TinyMCEEditorRef } from '../../components/TinyMCEEditor';
import PlaceholderLegend, { type PlaceholderLegendItem } from '../../components/PlaceholderLegend';
import type { TypographyCatalog } from '../../components/typography/TypographyField';
import TypographyPanel, { type TypographyTokens } from './TypographyPanel';

/**
 * Editor for one LP (webinar × page type): fixed form fields left, server-rendered live
 * preview right (one renderer = the contract — the iframe shows exactly what publish writes,
 * mirrors broker-hub ProfileEditorDialog).
 *
 * Lebenszyklus seit Plan 2026-10-22 (F3): EIN Hauptknopf „Veröffentlichen“. Ist die Seite noch
 * nicht die aktive Seite des Webinars, fuehrt er nach einer Rueckfrage beide bisherigen Schritte
 * nacheinander aus — PUT (Speichern) → POST /publish → POST /switch, der Switch nur, wenn Publish
 * ok war (dieselben Requests in derselben Reihenfolge wie vorher die zwei Knoepfe). Danach heisst
 * er „Änderungen veröffentlichen“ (nur PUT + /publish). Das getrennte „Live schalten“ bleibt fuer
 * den Admin (NurAdmin). „Als Entwurf speichern“ = nur PUT.
 */

/**
 * Bild-Upload + Adressfeld mit EIGENEM Hinweistext je Feld. `ImageUploadField` (geteilt) schreibt
 * fest „(z. B. SVG-Logo)“ unter jedes Feld — am Hintergrund- und Referentenbild ist das falsch
 * (Plan 2026-10-22 A0). Gleiches Verhalten: Ablegeflaeche schreibt die CDN-URL ueber onChange.
 */
const BildFeld: React.FC<{
  uploadUrl: string;
  value: string | null;
  onChange: (url: string) => void;
  urlHinweis: string;
  disabled?: boolean;
  disabledHint?: string;
}> = ({ uploadUrl, value, onChange, urlHinweis, disabled = false, disabledHint }) => (
  <Box>
    {disabled && disabledHint && (
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {disabledHint}
      </Typography>
    )}
    <ImageDropZone uploadUrl={uploadUrl} value={value} onUploaded={onChange} disabled={disabled} />
    <TextField
      disabled={disabled}
      label="Bildadresse"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      fullWidth
      sx={{ mt: 1 }}
      helperText={urlHinweis}
    />
  </Box>
);

export type LpPageType = 'doi' | 'recording' | 'signup';

interface PageRow {
  headline: string | null;
  subheadline: string | null;
  bullets: unknown;
  body_html: string | null;
  emblem_image_url: string | null;
  hero_bg_url: string | null;
  hero_bg_scrim: number | null;
  speaker_image_url: string | null;
  speaker_name: string | null;
  vimeo_video_id: string | null;
  cta_url: string | null;
  cta_text: string | null;
  cta_reveal_wallclock_s: number | null;
  cta_reveal_playback_s: number | null;
  exit_popup_headline: string | null;
  exit_popup_body_html: string | null;
  design_variant: 'electric' | 'dossier' | 'show' | 'bp' | null;
  scarcity_timer: number | null;
  typography: string | null;
  status: 'draft' | 'published';
  published_at: string | null;
  updated_at: string | null;
}

/** Raum-Kontext der Aufzeichnungsseite (node-app/src/services/lpRecordingCtaHint.ts). */
interface KaufknopfVorschlag {
  offerMinute: number | null;
  /** null = keine sichere Zahl (Angebot erst nach dem Video) — dann kein „Übernehmen". */
  playbackSekunde: number | null;
}

/**
 * Erscheint der Kaufknopf mit diesen Formularwerten sofort? Spiegel von `knopfSofort` im Server-Blatt: beide leer
 * oder eine 0 (die erste Schranke gewinnt). Die Regel steht dort; hier nur, weil der Hinweis dem TIPPEN folgt.
 */
export function knopfSofortImFormular(wall: string, playback: string): boolean {
  const w = wall.trim() === '' ? null : Number(wall);
  const p = playback.trim() === '' ? null : Number(playback);
  return w === 0 || p === 0 || (w === null && p === null);
}

interface FormState {
  headline: string;
  subheadline: string;
  bulletsText: string;
  body_html: string;
  emblem_image_url: string;
  hero_bg_url: string;
  /** null = Seitentyp-Standard erben, 0 = ausdruecklich kein Schleier, 1-100 = Deckkraft. */
  hero_bg_scrim: number | null;
  speaker_image_url: string;
  speaker_name: string;
  vimeo_video_id: string;
  cta_url: string;
  cta_text: string;
  cta_reveal_wallclock_s: string;
  cta_reveal_playback_s: string;
  exit_popup_headline: string;
  exit_popup_body_html: string;
  design_variant: 'electric' | 'dossier' | 'show' | 'bp';
  scarcity_timer: boolean;
  /** Nur der Override DIESER Seite; der Scope-Default kommt separat vom Server. */
  typography: TypographyTokens;
}

/** Ein Eintrag aus `GET …/typography-defaults` — Katalog + zentrale Werte je Scope. */
interface ScopeInfo {
  scope_key: string;
  tokens: TypographyTokens;
  catalog: TypographyCatalog;
}

/**
 * Spiegel von `typographyScopeFor` (node-app/src/services/LpPageService.ts) — bewusst drei
 * Zeilen Doppelung statt eines weiteren Endpunkts. Der Server bleibt die Autoritaet: er
 * validiert jeden Schreibvorgang gegen seinen eigenen Scope, ein Drift hier faellt also beim
 * Speichern auf und nicht erst auf der Live-Seite.
 */
const scopeFor = (pageType: LpPageType, variant: FormState['design_variant']): string =>
  pageType === 'signup' ? `signup:${variant}` : pageType;

/** Serverseitig aufgeloeste Typografie-Lage, wie sie `GET …/pages/:id/:type` mitliefert. */
interface TypographyInfo {
  scope: string;
  catalog: TypographyCatalog;
  defaults: TypographyTokens;
  override: TypographyTokens;
  effective: TypographyTokens;
  inherited: boolean;
}

const EMPTY_FORM: FormState = {
  headline: '',
  subheadline: '',
  bulletsText: '',
  body_html: '',
  emblem_image_url: '',
  hero_bg_url: '',
  hero_bg_scrim: null,
  speaker_image_url: '',
  speaker_name: '',
  vimeo_video_id: '',
  cta_url: '',
  cta_text: '',
  cta_reveal_wallclock_s: '',
  cta_reveal_playback_s: '',
  exit_popup_headline: '',
  exit_popup_body_html: '',
  design_variant: 'electric',
  scarcity_timer: true,
  typography: {},
};

/**
 * Die HTML-Felder, die im TinyMCE bearbeitet werden — und damit genau die, die gegen die
 * Normalisierung des Editors geschuetzt werden muessen (siehe pristineRef unten).
 */
const HTML_FIELDS = ['body_html', 'exit_popup_body_html'] as const;
type HtmlField = (typeof HTML_FIELDS)[number];

/**
 * Eine moegliche Vorlage: ein Webinar aus der Uebersichtsliste, das eine Seite dieses Typs hat.
 *
 * Der Typ steht bewusst HIER und nicht in `index.tsx`: das Scene importiert diesen Dialog schon
 * (index.tsx:25), der Rueckimport waere ein Zyklus. `WebinarRow` erfuellt die schmale Form
 * strukturell, `sources={rows}` passt deshalb ohne Cast.
 */
export interface LpTemplateSource {
  webinar_id: string;
  title: string;
  pages: Partial<Record<LpPageType, { status: 'draft' | 'published'; updated_at: string | null }>>;
}

interface Props {
  open: boolean;
  webinarId: string;
  webinarTitle: string;
  pageType: LpPageType;
  onClose: () => void;
  /** Fired after publish/switch so the list refreshes its status columns. */
  onChanged: () => void;
  /**
   * Kandidaten fuer „Inhalt uebernehmen" — die Liste, die das Eltern-Scene ohnehin geladen hat.
   * Bewusst kein eigener Request: die Auswahl braucht nur Titel und Status, den INHALT holt die
   * Uebernahme frisch ueber die Seiten-GET.
   */
  sources?: LpTemplateSource[];
  /**
   * Ist diese Seite schon die aktive Seite des Webinars (signup_url/doi_url/missed_webinar_url
   * zeigt hierher — `*_live` der Uebersicht)? undefined = unbekannt (Webinar nicht in der Liste):
   * dann veroeffentlicht der Hauptknopf nur und schaltet nicht um.
   */
  isLive?: boolean;
}

const LpPageEditorDialog: React.FC<Props> = ({
  open,
  webinarId,
  webinarTitle,
  pageType,
  sources,
  isLive,
  onClose,
  onChanged,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [status, setStatus] = useState<PageRow['status'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    severity: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [confirmSwitch, setConfirmSwitch] = useState<{ old_url: string | null } | null>(null);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  /** Rueckfrage vor dem ersten „Veröffentlichen“ (publizieren + aktiv schalten). */
  const [confirmVeroeffentlichen, setConfirmVeroeffentlichen] = useState(false);
  /** Rueckfrage „Ungespeicherte Änderungen verwerfen?“ beim Schliessen. */
  const [confirmVerwerfen, setConfirmVerwerfen] = useState(false);
  /**
   * Schliessen-Schutz (Persona-Runde 3). Bezugsstand = geladener bzw. zuletzt gespeicherter
   * Formularstand; null = noch nicht geladen (dann nie „ungespeichert“). `uebernommenRef` = eine
   * Vorlage wurde uebernommen und noch nicht gespeichert. Beide gehoeren zum DATENSATZ und werden
   * im Reset-Pfad des Ladeeffekts zurueckgesetzt.
   * ⚠ Nicht ueber TinyMCE-onChange: der Editor normalisiert beim Laden (`<p>`-Huelle) und meldet
   * das als Aenderung. Die HTML-Felder gelten deshalb als unveraendert, solange sie dem Bezugsstand
   * ODER der normalisierten Grundlinie (`normalizedRef`) entsprechen — dieselbe Regel wie
   * `htmlFieldForSave` (.claude/rules/react-frontend.md § TinyMCE, § Unsaved changes).
   */
  const bezugRef = useRef<FormState | null>(null);
  const uebernommenRef = useRef(false);
  /**
   * Lokale Korrektur von `isLive` nach Umschalten/Depublizieren, bis die Uebersicht neu geladen hat
   * (null = Prop gilt). Je Datensatz zurueckgesetzt.
   */
  const [liveOverride, setLiveOverride] = useState<boolean | null>(null);
  // Feldpruefung (Regeln + KI beim Verlassen) — nur auf der Anmeldeseite angezeigt. Hooks laufen
  // immer (Rules of Hooks); der Schluessel ist fest, die Seite wechselt nur ueber webinarId.
  const pruefHeadline = useFeldpruefung('lp.signup.headline', webinarId);
  const pruefSubheadline = useFeldpruefung('lp.signup.subheadline', webinarId);
  const pruefBullets = useFeldpruefung('lp.signup.bullets', webinarId);
  const resetHeadline = pruefHeadline.zuruecksetzen;
  const resetSubheadline = pruefSubheadline.zuruecksetzen;
  const resetBullets = pruefBullets.zuruecksetzen;
  const pruefBody = useFeldpruefung('lp.signup.body', webinarId);
  const resetBody = pruefBody.zuruecksetzen;
  /**
   * Feste Regeln (ohne KI) sofort nach dem Laden und nach „Übernehmen“, nicht erst beim Verlassen
   * eines Feldes. Persona-Runde 3: nach der Uebernahme standen Name, Datum und Referent des alten
   * Webinars in den Feldern, und der Editor sagte nichts. Ueber ein Ref, damit der Lade-Effekt und
   * `applyTemplate` keine neuen Abhaengigkeiten bekommen.
   */
  const regelnPruefenRef = useRef<
    (w: { headline: string; subheadline: string; bulletsText: string; body_html: string }) => void
  >(() => undefined);
  regelnPruefenRef.current = (w) => {
    if (pageType !== 'signup') return;
    pruefHeadline.pruefen(w.headline);
    pruefSubheadline.pruefen(w.subheadline);
    pruefBullets.pruefen(w.bulletsText);
    pruefBody.pruefen(w.body_html);
  };
  // Sendefaehigkeit der Bestaetigungsmail. Kommt vom Server (die Regel gehoert dem
  // ConfirmationSender, nicht dieser Ansicht) und wird VOR dem Umschalten gezeigt.
  const [confirmationGap, setConfirmationGap] = useState<
    'no_campaign' | 'campaign_incomplete' | null
  >(null);
  // Raum-Kontext fuer den Hinweis „Kaufknopf sofort sichtbar" (nur Aufzeichnungsseite, vom Server:
  // node-app/src/services/lpRecordingCtaHint.ts). Ob der Hinweis erscheint, entscheidet das AKTUELLE Formular.
  const [kaufknopfVorschlag, setKaufknopfVorschlag] = useState<KaufknopfVorschlag | null>(null);
  // Dialog instances survive "closing" (no open&&-guard at the call site would keep state) —
  // key the load per record and reset BEFORE fetching (.claude/rules/react-frontend.md).
  const loadKeyRef = useRef<string>('');
  const [typo, setTypo] = useState<TypographyInfo | null>(null);
  /** Seitentyp-Standard des Schleiers, vom Server. Bis zur Antwort neutral (0). */
  const [scrimDefault, setScrimDefault] = useState(0);
  /**
   * Platzhalter-Katalog samt aufgeloesten Werten — kommt aus der Seiten-GET, wird hier NICHT
   * nachgerechnet (die Aufloesungsregel gehoert dem Server, siehe LpPageService.getPage).
   */
  const [platzhalter, setPlatzhalter] = useState<
    Array<{ token: string; label: string; beschreibung: string; wert: string | null }>
  >([]);

  /**
   * Zuletzt fokussiertes Eingabefeld — das Ziel des Einfuegen-Knopfs.
   *
   * Der Mailing-Editor kommt ohne so etwas aus, weil dort nur EIN Fliesstext-Feld existiert. Hier
   * gibt es drei TinyMCE-Instanzen plus mehrere normale Felder, und ein Datum steht genauso oft
   * in der Headline wie im Fliesstext — ein Knopf, der nur ins letzte HTML-Feld schreibt, haette
   * genau den Fall nicht geloest, der den Vorfall ausgeloest hat.
   */
  const bodyEditorRef = useRef<TinyMCEEditorRef | null>(null);
  const popupEditorRef = useRef<TinyMCEEditorRef | null>(null);
  const scarcityEditorRef = useRef<TinyMCEEditorRef | null>(null);
  const letztesZiel = useRef<
    | { art: 'tiny'; editor: TinyMCEEditorRef | null }
    | { art: 'text'; feld: keyof FormState; el: HTMLInputElement | HTMLTextAreaElement }
    | null
  >(null);
  /**
   * Katalog + Defaults ALLER Scopes, damit ein Varianten-Wechsel im Dialog sofort den
   * richtigen Scope zeigt. Die Seiten-GET liefert nur den Scope der GESPEICHERTEN Variante —
   * wer im offenen Dialog auf „Best Practice" wechselt, saehe sonst weiter Katalog, geerbte
   * Werte und Beschriftung der alten Variante (und das Schriftart-Feld aktiv, obwohl bp
   * Webfonts sperrt). Gefunden im Review 2026-08-19.
   */
  const [scopes, setScopes] = useState<Record<string, ScopeInfo> | null>(null);
  /** Referent aus den Webinar-Grunddaten (host_name) — nur als Vorschlag ins Feld, nie gespeichert. */
  const [hostName, setHostName] = useState<string | null>(null);
  /** Referent weicht von den Grunddaten ab — typisch nach „Übernehmen“ aus einem anderen Webinar. */
  const referentBefunde = useMemo<FeldBefund[] | undefined>(() => {
    const name = form.speaker_name.trim().toLowerCase();
    const soll = (hostName || '').toLowerCase();
    if (!soll || !name || name.includes(soll) || soll.includes(name)) return undefined;
    return [
      {
        schwere: 'warnung',
        quelle: 'regel',
        code: 'referent_abweichend',
        text: `In den Grunddaten steht „${hostName}“ — stammt dieser Name aus einer übernommenen Seite?`,
      },
    ];
  }, [form.speaker_name, hostName]);
  /** Im Waehler markierte Vorlage (webinar_id der Quelle), noch nicht uebernommen. */
  const [templateId, setTemplateId] = useState('');
  /** Offen = Uebernahme wartet auf Bestaetigung, weil das Ziel schon Inhalt traegt. */
  const [confirmTemplate, setConfirmTemplate] = useState<LpTemplateSource | null>(null);

  /**
   * DER RIEGEL GEGEN STILLEN HASH-DRIFT.
   *
   * TinyMCE normalisiert beim Laden (haengt `<p>` um nackten Text, sortiert Attribute um). Der
   * Wrapper faehrt controlled und reicht `onEditorChange` ungefiltert durch — der normalisierte
   * Stand landet also im Formularstate, ohne dass jemand getippt hat. Und weil dieser Dialog
   * beim Speichern IMMER das ganze Formular PUTet, wuerde schon eine reine Headline-Aenderung
   * diese Normalisierung mitschreiben: neuer Inhalt, neuer build_hash, obwohl niemand den Text
   * angefasst hat.
   *
   * Der Riegel vergleicht INHALTE, nicht Ereignisse: `normalizedRef` haelt, was TinyMCE beim
   * Laden aus dem Serverwert gemacht hat. Solange der Formularwert damit identisch ist, hat
   * niemand editiert — und `buildBody` schickt dann den ORIGINAL-Serverstring zurueck.
   *
   * ⚠ Der erste Versuch klassifizierte Ereignisse (`onUserEdit` aus input/keyup/ExecCommand/…)
   * und ist MESSBAR durchgefallen: TinyMCE feuert nach `init` von sich aus
   * `ExecCommand(mceFocus)`, wodurch beide Felder als beruehrt galten und eine reine
   * Headline-Aenderung die Normalisierung mitgespeichert hat (gemessen 2026-08-19 auf WbbBPM).
   * Die Vorschau darf weiterhin normalisiert rendern — sie schreibt nichts.
   */
  const pristineRef = useRef<Record<HtmlField, string | null>>({
    body_html: null,
    exit_popup_body_html: null,
  });
  const normalizedRef = useRef<Record<HtmlField, string | null>>({
    body_html: null,
    exit_popup_body_html: null,
  });
  const setNormalized = useCallback((field: HtmlField, html: string) => {
    normalizedRef.current[field] = html;
  }, []);

  const base = useMemo(
    () => `/api/webinar-lp/pages/${encodeURIComponent(webinarId)}/${pageType}`,
    [webinarId, pageType],
  );

  const refreshPreview = useCallback(async () => {
    const res = await fetchData<{ html: string }>(`${base}/preview`);
    if (res.success && res.data) setPreviewHtml(res.data.html);
  }, [base]);

  useEffect(() => {
    if (!open) return;
    const key = `${webinarId}:${pageType}`;
    if (loadKeyRef.current === key) return;
    loadKeyRef.current = key;
    setForm(EMPTY_FORM);
    setPreviewHtml(null);
    setMessage(null);
    setStatus(null);
    setTypo(null);
    setScopes(null);
    setScrimDefault(0);
    setHostName(null);
    setTemplateId('');
    setConfirmTemplate(null);
    setLiveOverride(null);
    setConfirmVeroeffentlichen(false);
    setConfirmVerwerfen(false);
    setKaufknopfVorschlag(null);
    bezugRef.current = null;
    uebernommenRef.current = false;
    // Befunde gehoeren zum Datensatz: sonst stuende die Pruefung der vorigen Seite am Feld.
    resetHeadline();
    resetSubheadline();
    resetBullets();
    resetBody();
    // Beide Register gehoeren zum Datensatz, nicht zum Dialog: ohne Reset traegt die naechste
    // Seite die Beruehrt-Markierung der vorigen und ihr Original-HTML.
    pristineRef.current = { body_html: null, exit_popup_body_html: null };
    normalizedRef.current = { body_html: null, exit_popup_body_html: null };
    // Aus demselben Grund das Einfuege-Ziel: es zeigt sonst auf ein Feld der VORIGEN Seite, und
    // ein Einfuegen ohne vorherigen Feldklick uebernaehme dessen alten Wert in die neue Seite,
    // statt den Hinweis zu zeigen (Codex-Gegenlesung 2026-09-04).
    letztesZiel.current = null;
    setLoading(true);
    let cancelled = false;
    (async () => {
      const res = await fetchData<{
        page: PageRow | null;
        typography?: TypographyInfo;
        scrimDefault?: number;
        confirmation_gap?: 'no_campaign' | 'campaign_incomplete' | null;
        placeholders?: Array<{
          token: string;
          label: string;
          beschreibung: string;
          wert: string | null;
        }>;
        kaufknopf_vorschlag?: KaufknopfVorschlag | null;
      }>(base);
      if (cancelled) return;
      setLoading(false);
      if (!res.success) {
        setMessage({ severity: 'error', text: res.error || 'Laden fehlgeschlagen' });
        return;
      }
      // Ohne Seite ist das leere Formular der Bezugsstand; mit Seite wird er unten ueberschrieben.
      bezugRef.current = EMPTY_FORM;
      if (res.data?.typography) setTypo(res.data.typography);
      setConfirmationGap(res.data?.confirmation_gap ?? null);
      setKaufknopfVorschlag(res.data?.kaufknopf_vorschlag ?? null);
      // Der Standard kommt vom Server, damit hier keine zweite Kopie von SCRIM_DEFAULT_PCT
      // entsteht, die beim naechsten Renderer-Umbau still auseinanderlaeuft.
      if (typeof res.data?.scrimDefault === 'number') setScrimDefault(res.data.scrimDefault);
      setPlatzhalter(res.data?.placeholders ?? []);
      // Zweiter, kleiner Request: liefert alle sechs Scopes samt Katalog. Bewusst NICHT in die
      // Seiten-GET gepresst — die Liste ist seiten-unabhaengig und wird auch vom Defaults-Tab
      // gebraucht. Faellt sie aus, bleibt der Dialog auf dem Scope der Seiten-GET stehen
      // (schlechter, aber nicht kaputt).
      // Referent der Grunddaten fuer den Knopf „Aus den Grunddaten übernehmen“ — still bei Fehler.
      fetchData<{ host_name?: string | null }>(
        `/api/webinar-management/webinars/${encodeURIComponent(webinarId)}`,
      ).then((r) => {
        const n = r.success ? r.data?.host_name : null;
        if (!cancelled && typeof n === 'string' && n.trim()) setHostName(n.trim());
      });
      fetchData<ScopeInfo[]>('/api/webinar-lp/typography-defaults').then((r) => {
        if (cancelled || !r.success || !r.data) return;
        setScopes(Object.fromEntries(r.data.map((row) => [row.scope_key, row])));
      });
      const p = res.data?.page;
      if (p) {
        setStatus(p.status);
        pristineRef.current = {
          body_html: p.body_html || '',
          exit_popup_body_html: p.exit_popup_body_html || '',
        };
        const geladen: FormState = {
          headline: p.headline || '',
          subheadline: p.subheadline || '',
          bulletsText: Array.isArray(p.bullets) ? (p.bullets as string[]).join('\n') : '',
          body_html: p.body_html || '',
          emblem_image_url: p.emblem_image_url || '',
          hero_bg_url: p.hero_bg_url || '',
          hero_bg_scrim: p.hero_bg_scrim ?? null,
          speaker_image_url: p.speaker_image_url || '',
          speaker_name: p.speaker_name || '',
          vimeo_video_id: p.vimeo_video_id || '',
          cta_url: p.cta_url || '',
          cta_text: p.cta_text || '',
          cta_reveal_wallclock_s: p.cta_reveal_wallclock_s?.toString() || '',
          cta_reveal_playback_s: p.cta_reveal_playback_s?.toString() || '',
          exit_popup_headline: p.exit_popup_headline || '',
          exit_popup_body_html: p.exit_popup_body_html || '',
          design_variant: p.design_variant || 'electric',
          scarcity_timer: p.scarcity_timer !== 0,
          typography: res.data?.typography?.override || {},
        };
        bezugRef.current = geladen;
        setForm(geladen);
        regelnPruefenRef.current({
          headline: p.headline || '',
          subheadline: p.subheadline || '',
          bulletsText: Array.isArray(p.bullets) ? (p.bullets as string[]).join('\n') : '',
          body_html: p.body_html || '',
        });
      }
      refreshPreview();
    })();
    return () => {
      cancelled = true;
      // StrictMode mountet Effekte im DEV doppelt: Lauf 1 wird hier cancelled, Lauf 2 darf
      // nicht am loadKeyRef-Guard abprallen (sonst Spinner für immer — Fund 2026-08-13).
      loadKeyRef.current = '';
    };
  }, [
    open,
    webinarId,
    pageType,
    base,
    refreshPreview,
    resetHeadline,
    resetSubheadline,
    resetBullets,
    resetBody,
  ]);

  // Allow re-load when the dialog is reopened for the same record after a close.
  useEffect(() => {
    if (!open) loadKeyRef.current = '';
  }, [open]);

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /** Merkt ein normales Eingabefeld als Einfuege-Ziel. An jedes platzhalterfaehige Feld haengen. */
  const merkeFeld =
    (feld: keyof FormState) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      letztesZiel.current = { art: 'text', feld, el: e.target };
    };

  /**
   * Merkt eine der drei TinyMCE-Instanzen als Einfuege-Ziel.
   *
   * Nimmt das REF, nicht dessen Wert: beim Rendern ist `.current` noch null, der Editor meldet
   * sich erst mit onInit. Ausgelesen wird deshalb erst im Fokus-Moment.
   */
  const merkeEditor = (ref: React.MutableRefObject<TinyMCEEditorRef | null>) => () => {
    letztesZiel.current = { art: 'tiny', editor: ref.current };
  };

  /**
   * Fuegt einen Platzhalter an der Cursorposition des zuletzt fokussierten Feldes ein.
   *
   * ⚠ Beim normalen Textfeld muss der Cursor danach von Hand hinter das Token gesetzt werden:
   * der State ist kontrolliert, React rendert das Feld neu, und der Cursor landet sonst am Ende
   * — das naechste Einfuegen liefe dann an die falsche Stelle. Das setSelectionRange muss NACH
   * dem Rendern laufen, deshalb requestAnimationFrame.
   *
   * Beim TinyMCE ist nichts weiter zu tun: insertContent loest ein change-Event aus, der Wrapper
   * meldet es ueber onChange, und damit weicht der Wert vom normalisierten Serverstand ab —
   * htmlFieldForSave schickt also den neuen Text und nicht das Original zurueck.
   */
  const platzhalterEinfuegen = useCallback((token: string) => {
    const ziel = letztesZiel.current;
    if (!ziel) {
      setMessage({
        severity: 'info',
        text: 'Bitte zuerst in das Feld klicken, in das der Platzhalter soll.',
      });
      return;
    }
    if (ziel.art === 'tiny') {
      ziel.editor?.insertContent(token);
      ziel.editor?.focus();
      return;
    }
    const { el, feld } = ziel;
    const von = el.selectionStart ?? el.value.length;
    const bis = el.selectionEnd ?? von;
    const neu = el.value.slice(0, von) + token + el.value.slice(bis);
    setForm((prev) => ({ ...prev, [feld]: neu }));
    const cursor = von + token.length;
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(cursor, cursor);
    });
  }, []);

  /** Katalog -> Legenden-Eintraege. Die Klammern kommen erst hier dazu (der Server liefert nackte Token). */
  const legendenEintraege: PlaceholderLegendItem[] = useMemo(
    () =>
      platzhalter.map((p) => ({
        placeholder: `{${p.token}}`,
        label: p.label,
        description: p.beschreibung,
        value: p.wert,
      })),
    [platzhalter],
  );

  /**
   * Der eigentliche Riegel: solange ein HTML-Feld kein `onUserEdit` gesehen hat, gilt der Wert,
   * den der Server geliefert hat — nicht der (moeglicherweise normalisierte) Formularstate.
   */
  const htmlFieldForSave = useCallback((field: HtmlField, current: string): string | null => {
    const original = pristineRef.current[field];
    const normalized = normalizedRef.current[field];
    if (original !== null && normalized !== null && current === normalized) {
      return original || null;
    }
    return current || null;
  }, []);

  const buildBody = useCallback(
    (f: FormState) => ({
      headline: f.headline,
      subheadline: f.subheadline || null,
      bullets: f.bulletsText
        .split('\n')
        .map((b) => b.trim())
        .filter(Boolean),
      // Unberuehrte HTML-Felder gehen als Original-Serverstring zurueck (siehe pristineRef).
      body_html: htmlFieldForSave('body_html', f.body_html),
      emblem_image_url: f.emblem_image_url || null,
      hero_bg_url: f.hero_bg_url || null,
      hero_bg_scrim: f.hero_bg_scrim,
      speaker_image_url: f.speaker_image_url || null,
      speaker_name: f.speaker_name || null,
      vimeo_video_id: f.vimeo_video_id || null,
      cta_url: f.cta_url || null,
      cta_text: f.cta_text || null,
      cta_reveal_wallclock_s:
        f.cta_reveal_wallclock_s === '' ? null : Number(f.cta_reveal_wallclock_s),
      cta_reveal_playback_s:
        f.cta_reveal_playback_s === '' ? null : Number(f.cta_reveal_playback_s),
      exit_popup_headline: f.exit_popup_headline || null,
      exit_popup_body_html: htmlFieldForSave('exit_popup_body_html', f.exit_popup_body_html),
      design_variant: f.design_variant,
      scarcity_timer: f.scarcity_timer,
      typography: f.typography,
    }),
    [htmlFieldForSave],
  );

  // -------------------------------------------------------------------------
  // Inhalt aus einer anderen Webinar-Seite uebernehmen
  // -------------------------------------------------------------------------

  /**
   * Waehlbare Vorlagen: gleicher Seitentyp, anderes Webinar, zuletzt geaenderte zuerst.
   *
   * Die Liste kommt aus dem Eltern-Scene und traegt damit dessen Grenzen: nur `active = 1` und
   * maximal 200 Webinare (LpPageService.ts:299-310), und sie laedt erst nach Publish/Switch neu.
   * Eine gerade gespeicherte Seite taucht also erst nach einem Neuladen als Vorlage auf.
   */
  const templateOptions = useMemo(
    () =>
      (sources || [])
        .filter((s) => s.webinar_id !== webinarId && Boolean(s.pages?.[pageType]))
        .sort((a, b) =>
          String(b.pages[pageType]?.updated_at || '').localeCompare(
            String(a.pages[pageType]?.updated_at || ''),
          ),
        ),
    [sources, webinarId, pageType],
  );

  /**
   * Traegt das Formular schon etwas, das eine Uebernahme zerstoeren wuerde? Geprueft wird gegen
   * EMPTY_FORM, also feldweise und ohne Liste, die beim naechsten neuen Feld veraltet.
   * `design_variant` und `scarcity_timer` zaehlen NICHT: sie haben von Haus aus einen Wert und
   * wuerden jede leere Seite als „gefuellt" melden.
   */
  const targetHasContent = useMemo(() => {
    const ignored: Array<keyof FormState> = ['design_variant', 'scarcity_timer'];
    return (Object.keys(EMPTY_FORM) as Array<keyof FormState>).some((k) => {
      if (ignored.includes(k)) return false;
      if (k === 'typography') return Object.keys(form.typography).length > 0;
      return form[k] !== EMPTY_FORM[k];
    });
  }, [form]);

  /**
   * Holt den Inhalt der Vorlage frisch vom Server und legt ihn ins Formular. Schreibt NICHTS —
   * gespeichert wird weiter ueber den Speichern-Knopf, Ruecknahme ist das Schliessen des Dialogs.
   */
  const applyTemplate = useCallback(
    async (source: LpTemplateSource) => {
      setBusy('template');
      setMessage(null);
      const res = await fetchData<{ page: PageRow | null; typography?: TypographyInfo }>(
        `/api/webinar-lp/pages/${encodeURIComponent(source.webinar_id)}/${pageType}`,
      );
      setBusy(null);
      if (!res.success) {
        setMessage({ severity: 'error', text: res.error || 'Vorlage konnte nicht geladen werden' });
        return;
      }
      const p = res.data?.page;
      if (!p) {
        setMessage({
          severity: 'error',
          text: `„${source.title}“ hat keine Seite dieses Typs mehr — nichts übernommen.`,
        });
        return;
      }

      /**
       * DER RIEGEL MUSS FUER DIESE FELDER AUS.
       *
       * `htmlFieldForSave` gibt den ORIGINAL-Serverstring des Ziels zurueck, sobald der
       * Formularwert der TinyMCE-Grundlinie entspricht. Normalisiert der uebernommene Body auf
       * genau diese Grundlinie (gleicher Inhalt, andere Rohfassung), wuerde die Uebernahme beim
       * Speichern still verworfen. Mit `null` scheitert schon `original !== null`, der Zweig kann
       * also gar nicht mehr feuern. Unbedenklich, weil hier tatsaechlich jemand den Inhalt
       * geaendert hat — und `save` setzt beide Register danach ohnehin neu.
       */
      pristineRef.current = { body_html: null, exit_popup_body_html: null };
      normalizedRef.current = { body_html: null, exit_popup_body_html: null };
      uebernommenRef.current = true;

      setForm((prev) => ({
        ...prev,
        headline: p.headline || '',
        subheadline: p.subheadline || '',
        bulletsText: Array.isArray(p.bullets) ? (p.bullets as string[]).join('\n') : '',
        body_html: p.body_html || '',
        emblem_image_url: p.emblem_image_url || '',
        hero_bg_url: p.hero_bg_url || '',
        hero_bg_scrim: p.hero_bg_scrim ?? null,
        speaker_image_url: p.speaker_image_url || '',
        speaker_name: p.speaker_name || '',
        cta_text: p.cta_text || '',
        exit_popup_headline: p.exit_popup_headline || '',
        exit_popup_body_html: p.exit_popup_body_html || '',
        // Nur die Anmeldeseite hat Varianten. Fuer DOI und Aufzeichnung ignoriert
        // `typographyScopeFor` die Spalte (LpPageService.ts:583-594) — sie zu uebernehmen haette
        // dort keinerlei Wirkung und wuerde nur Rauschen in die Zeile schreiben.
        ...(pageType === 'signup'
          ? {
              design_variant: p.design_variant || 'electric',
              scarcity_timer: p.scarcity_timer !== 0,
            }
          : {}),
        // Der Seiten-Override der Quelle, nie ein Scope-Default. Weil `design_variant` bei signup
        // mitkommt, liegen Quelle und Ziel im selben Scope und der Server-Sanitizer wirft nicht.
        typography: res.data?.typography?.override || {},
        /**
         * AKTIV LEEREN, nicht bloss auslassen. `buildBody` schickt IMMER alle Felder und der
         * Upsert schreibt alle Spalten — ein Auslassen wuerde auf einer gefuellten Seite die
         * ALTEN Werte stehen lassen und damit fremden Text ueber eigenes Video legen.
         * Ungefaehrlich: `cta_url` rendert nur die Aufzeichnungsseite, und dort faellt sie auf
         * `meta.orderUrl` des ZIEL-Webinars zurueck (lpTemplates.ts:877). Die beiden
         * Einblendzeiten gehoeren zum Video, das bewusst nicht mitkommt.
         */
        cta_url: '',
        vimeo_video_id: '',
        cta_reveal_wallclock_s: '',
        cta_reveal_playback_s: '',
        /**
         * ZWEITE Klasse desselben Riegels: Felder, fuer die der ZIELtyp keinen Platz hat. Der
         * Renderer benutzt sie dort nicht (`subheadline` nur in den Anmeldeseiten-Templates,
         * `emblemImageUrl` nicht in renderRecordingPage, `ctaText`/`exitPopup*` nicht in
         * renderDoiPage) und der Editor blendet sie aus — uebernommen wuerden sie zu
         * unsichtbarem Fremdtext, den man ueber die Oberflaeche nicht mehr loswird. Dieselbe
         * Entscheidung steht fuer die Bildslots schon weiter unten: „ein Knopf ohne Wirkung
         * waere schlimmer als keiner".
         *
         * GELEERT, nicht ausgelassen — sonst haengt das Ergebnis einer Uebernahme am
         * unsichtbaren Vorzustand des Ziels.
         */
        ...(pageType === 'signup' ? {} : { subheadline: '' }),
        ...(pageType === 'doi'
          ? { cta_text: '', exit_popup_headline: '', exit_popup_body_html: '' }
          : {}),
        ...(pageType === 'recording' ? { emblem_image_url: '' } : {}),
      }));
      regelnPruefenRef.current({
        headline: p.headline || '',
        subheadline: p.subheadline || '',
        bulletsText: Array.isArray(p.bullets) ? (p.bullets as string[]).join('\n') : '',
        body_html: p.body_html || '',
      });
      setMessage({
        severity: 'success',
        text: `Inhalt aus „${source.title}“ übernommen — noch nicht gespeichert.${
          pageType === 'recording' ? ' Kaufknopf-Ziel, Video und Einblendzeiten wurden geleert.' : ''
        }`,
      });
    },
    [pageType],
  );

  /** Klick auf „Übernehmen": bei gefülltem Ziel erst fragen, sonst direkt anwenden. */
  const onTemplateApply = () => {
    const source = templateOptions.find((s) => s.webinar_id === templateId);
    if (!source) return;
    if (targetHasContent) setConfirmTemplate(source);
    else applyTemplate(source);
  };

  // Live-Vorschau: debounced Server-Render des UNGESPEICHERTEN Formularstands über denselben
  // Renderer wie Publish (preview-draft schreibt nichts). Sequenz-Guard verwirft stale
  // Antworten, wenn schnell weitergetippt wird.
  const previewSeqRef = useRef(0);
  useEffect(() => {
    if (!open || loading) return;
    const seq = ++previewSeqRef.current;
    const t = setTimeout(async () => {
      const res = await postData<{ html: string }>(`${base}/preview-draft`, buildBody(form));
      if (previewSeqRef.current !== seq) return;
      if (res.success && res.data) setPreviewHtml(res.data.html);
    }, 600);
    return () => clearTimeout(t);
  }, [open, loading, form, base, buildBody]);

  const save = async (): Promise<boolean> => {
    setBusy('save');
    setMessage(null);
    const gesendetesFormular = form;
    const sent = buildBody(form);
    const res = await fetchData<{ warnings: string[] }>(base, {
      method: 'PUT',
      body: JSON.stringify(sent),
    });
    setBusy(null);
    if (!res.success) {
      setMessage({ severity: 'error', text: res.error || 'Speichern fehlgeschlagen' });
      return false;
    }
    // Was gespeichert wurde, IST ab jetzt der Serverstand — sonst schickt der naechste Save
    // wieder den alten Originalstring und macht die gerade gespeicherte Aenderung rueckgaengig.
    pristineRef.current = {
      body_html: sent.body_html || '',
      exit_popup_body_html: sent.exit_popup_body_html || '',
    };
    // Die Grundlinie muss mitwandern: nach dem Speichern ist der aktuelle Editor-Inhalt der
    // neue "unveraenderte" Zustand, sonst wuerde der naechste Save ihn wieder zurueckdrehen.
    normalizedRef.current = {
      body_html: form.body_html,
      exit_popup_body_html: form.exit_popup_body_html,
    };
    // Schliessen-Schutz: der gespeicherte Stand ist der neue Bezug (was danach getippt wurde,
    // bleibt „ungespeichert“, weil es vom Schnappschuss abweicht).
    bezugRef.current = gesendetesFormular;
    uebernommenRef.current = false;
    const warnings = res.data?.warnings || [];
    setMessage({
      severity: warnings.length ? 'info' : 'success',
      text: warnings.length ? `Gespeichert. Hinweise: ${warnings.join(' · ')}` : 'Gespeichert.',
    });
    refreshPreview();
    return true;
  };

  /**
   * Heutiges „Speichern + Publizieren“: PUT, dann POST /publish. null = fehlgeschlagen, sonst die Hinweise des
   * Publish — `veroeffentlichenUndUmschalten` reicht sie an die Schlussmeldung weiter (sonst ueberschriebe das
   * anschliessende Umschalten sie, Codex 27.10.).
   */
  const publish = async (): Promise<string[] | null> => {
    if (!(await save())) return null;
    setBusy('publish');
    const res = await postData<{ url: string; warnings: string[] }>(`${base}/publish`, {});
    setBusy(null);
    if (!res.success) {
      setMessage({ severity: 'error', text: res.error || 'Veröffentlichen fehlgeschlagen' });
      return null;
    }
    setStatus('published');
    const warnings = res.data?.warnings || [];
    setMessage({
      severity: 'success',
      text: `Veröffentlicht: ${res.data?.url}${warnings.length ? ` — Hinweise: ${warnings.join(' · ')}` : ''}`,
    });
    onChanged();
    return warnings;
  };

  /** Heutiges „Live schalten“: POST /switch. true = ok. `vorherigeHinweise` = Hinweise des Publish davor. */
  const doSwitch = async (
    nachVeroeffentlichen = false,
    vorherigeHinweise: string[] = [],
  ): Promise<boolean> => {
    setConfirmSwitch(null);
    setBusy('switch');
    const res = await postData<{
      column: string;
      old_url: string | null;
      new_url: string;
      warnings: string[];
    }>(`${base}/switch`, {});
    setBusy(null);
    if (!res.success) {
      setMessage({
        severity: 'error',
        text: nachVeroeffentlichen
          ? `Veröffentlicht, aber noch nicht als aktive ${typeLabel} geschaltet: ${res.error || 'unbekannter Fehler'}. Die Seite ist im Netz; bitte erneut „Veröffentlichen“ oder den Admin fragen.`
          : res.error || 'Live schalten fehlgeschlagen',
      });
      return false;
    }
    setLiveOverride(true);
    const warnings = [...vorherigeHinweise, ...(res.data?.warnings || [])];
    setMessage({
      severity: 'success',
      text: `${nachVeroeffentlichen ? 'Veröffentlicht und live' : 'Live geschaltet'}: ${res.data?.new_url} ist jetzt die aktive ${typeLabel} des Webinars.${warnings.length ? ` Hinweise: ${warnings.join(' · ')}` : ''}`,
    });
    onChanged();
    return true;
  };

  /** Erster Klick auf „Veröffentlichen“ bei noch nicht aktiver Seite: beide Schritte nacheinander. */
  const veroeffentlichenUndUmschalten = async () => {
    setConfirmVeroeffentlichen(false);
    const hinweise = await publish();
    if (hinweise === null) return;
    await doSwitch(true, hinweise);
  };

  /**
   * Depublizieren (2026-09-05): Datei(en) weg, Status zurück auf Entwurf, URL-Spalte des Webinars
   * zurück — der Bewerbungsstatus kippt sofort auf „Unvollständig". Inhalt bleibt erhalten.
   */
  const doUnpublish = async () => {
    setConfirmUnpublish(false);
    setBusy('unpublish');
    const res = await postData<{
      files_removed: string[];
      url_reset: { column: string; old_url: string | null; new_url: string | null } | null;
    }>(`${base}/unpublish`, {});
    setBusy(null);
    if (!res.success) {
      setMessage({ severity: 'error', text: res.error || 'Vom Netz nehmen fehlgeschlagen' });
      return;
    }
    setStatus('draft');
    const reset = res.data?.url_reset;
    if (reset) setLiveOverride(false);
    setMessage({
      severity: 'info',
      text:
        `Vom Netz genommen: ${res.data?.files_removed.length ?? 0} Datei(en) entfernt` +
        (reset
          ? ` — das Webinar zeigt jetzt auf ${reset.new_url ?? 'keine Seite'}`
          : ' — das Webinar zeigte nicht auf diese Seite, dort ändert sich nichts'),
    });
    onChanged();
  };

  const previewSrcDoc = previewHtml;
  const isRecording = pageType === 'recording';
  const isSignup = pageType === 'signup';
  const isDoi = pageType === 'doi';
  // Gespeicherter Wert schlaegt den Standard; `??` statt `||`, sonst faellt eine ausdrueckliche
  // 0 („kein Schleier") auf den Standard zurueck und der Schalter springt von selbst zurueck.
  const scrimPct = form.hero_bg_scrim ?? scrimDefault;
  const scrimOn = scrimPct > 0;
  const pageUrl = `https://lp.finanztrends.de/w/${webinarId}/${
    { signup: 'anmeldung', doi: 'bestaetigung', recording: 'aufzeichnung' }[pageType]
  }/`;
  const typeLabel = isSignup
    ? 'Anmeldeseite'
    : isRecording
      ? 'Aufzeichnungsseite'
      : 'Bestätigungsseite';
  const istLive = liveOverride ?? isLive;
  /** Erstes Veröffentlichen schaltet zusaetzlich um — nur wenn sicher bekannt ist, dass sie es noch nicht ist. */
  const ersteVeroeffentlichung = istLive === false;
  const hauptKnopfText = istLive ? 'Änderungen veröffentlichen' : BEGRIFF.veroeffentlichen;
  const doiHinweis = isSignup
    ? ' Danach auch die Bestätigungsseite veröffentlichen (in der Liste Spalte „Bestätigung“ anklicken).'
    : '';
  /** Anmelde- UND Bestaetigungsseite live (+ Rest von „Bewerbbar?“) schalten Versand + Werbung ein. */
  const freigabeHinweis = isRecording
    ? ''
    : ' Sind Anmelde- UND Bestätigungsseite live (und die übrigen Punkte unter „Bewerbbar?“ im Webinar-Fahrplan erledigt), schalten sich der automatische Versand der Erinnerungsmails und die Werbung für das Webinar ein.';
  const hauptKnopfHinweisKern = istLive
    ? `Diese Seite ist die aktive ${typeLabel} des Webinars — „Änderungen veröffentlichen“ bringt deine Änderungen ins Netz.`
    : ersteVeroeffentlichung
      ? `„Veröffentlichen“ stellt die Seite ins Netz und macht sie zur aktiven ${typeLabel} des Webinars (mit Rückfrage).`
      : 'Webinar nicht in der Übersicht — „Veröffentlichen“ stellt die Seite nur ins Netz; aktiv schalten kann der Admin.';
  const hauptKnopfHinweis = hauptKnopfHinweisKern + doiHinweis + freigabeHinweis;
  /** Gibt es Ungespeichertes? Regel siehe `bezugRef`. */
  const hatUngespeichertes = (): boolean => {
    const bezug = bezugRef.current;
    if (!bezug) return false;
    if (uebernommenRef.current) return true;
    return (Object.keys(EMPTY_FORM) as Array<keyof FormState>).some((k) => {
      if (k === 'typography') return JSON.stringify(form.typography) !== JSON.stringify(bezug.typography);
      if ((HTML_FIELDS as readonly string[]).includes(k)) {
        const f = k as HtmlField;
        return form[f] !== bezug[f] && form[f] !== normalizedRef.current[f];
      }
      return form[k] !== bezug[k];
    });
  };
  const versucheSchliessen = () => {
    if (busy !== null) return;
    if (hatUngespeichertes()) setConfirmVerwerfen(true);
    else onClose();
  };

  const onHauptKnopf = () => {
    if (ersteVeroeffentlichung) setConfirmVeroeffentlichen(true);
    else void publish();
  };

  return (
    <Dialog open={open} onClose={versucheSchliessen} maxWidth="xl" fullWidth>
      <DialogTitle>
        {typeLabel} — {webinarTitle}
        {status && (
          <Chip
            size="small"
            sx={{ ml: 1 }}
            label={
              istLive && status === 'published'
                ? BEGRIFF.live
                : status === 'published'
                  ? BEGRIFF.veroeffentlicht
                  : BEGRIFF.entwurf
            }
            color={istLive && status === 'published' ? 'success' : status === 'published' ? 'info' : 'default'}
          />
        )}
        {status === 'published' && (
          <Link
            href={pageUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ ml: 1.5, fontSize: '0.85rem', verticalAlign: 'middle' }}
          >
            {pageUrl}
            <OpenInNewIcon sx={{ fontSize: 13, ml: 0.4, verticalAlign: 'text-top' }} />
          </Link>
        )}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box p={4} textAlign="center">
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box display="flex" sx={{ minHeight: '70vh' }}>
            {/* Form */}
            <Box
              flex="0 0 44%"
              p={2}
              display="flex"
              flexDirection="column"
              gap={1}
              sx={{ overflowY: 'auto', maxHeight: '75vh', bgcolor: '#f7f8fa' }}
            >
              {message && <Alert severity={message.severity}>{message.text}</Alert>}
              <Abschnitt
                titel="1. Inhalt übernehmen (empfohlen)"
                art="optional"
                erklaerung="Am schnellsten: Texte und Gestaltung vom letzten Webinar übernehmen. Danach ALLE Texte an dieses Webinar anpassen — die Prüfung markiert fremde Webinartitel, fremde Daten und einen abweichenden Referenten, das Thema prüft sie nicht. Gespeichert wird erst mit „Als Entwurf speichern“ oder „Veröffentlichen“."
              >
                {templateOptions.length > 0 ? (
                  <Box display="flex" flexDirection="column" gap={0.5}>
                    <Box display="flex" gap={1} alignItems="center">
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel id="lp-template-label">Inhalt übernehmen aus …</InputLabel>
                        <Select
                          labelId="lp-template-label"
                          label="Inhalt übernehmen aus …"
                          value={templateId}
                          onChange={(e) => setTemplateId(e.target.value as string)}
                        >
                          {templateOptions.map((src) => (
                            <MenuItem key={src.webinar_id} value={src.webinar_id}>
                              {src.title}
                              {src.pages[pageType]?.status === 'published' ? '' : ' (Entwurf)'}
                              {/* Die webinar_id muss mit: zwei Webinare koennen denselben Titel
                                  tragen (gemessen 2026-08-20: E3y5oP und gBFUAq heissen beide
                                  „Chipkrieg USA vs China - Die neue NVIDIA") und waeren sonst im
                                  Waehler nicht auseinanderzuhalten. */}
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 0.75 }}
                              >
                                {src.webinar_id}
                              </Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={!templateId || busy !== null}
                        onClick={onTemplateApply}
                      >
                        {busy === 'template' ? 'Lädt…' : 'Übernehmen'}
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Übernimmt Texte, Bilder und Gestaltung.
                      {isRecording
                        ? ' Kaufknopf-Ziel, Video und die Einblendzeiten bleiben leer — sie gehören zum jeweiligen Webinar.'
                        : ''}{' '}
                      Die Liste zeigt die zuletzt geänderten Seiten zuerst.
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Es gibt noch keine andere {typeLabel} als Vorlage — die Felder unten von Hand
                    ausfüllen.
                  </Typography>
                )}
              </Abschnitt>

              <Abschnitt
                titel="2. Texte"
                erklaerung="Was der Besucher liest. Leere Felder sind kein Fehler — dann gilt der jeweils genannte Standard. Die Vorschau rechts zeigt sofort, wie es aussieht."
              >
                {legendenEintraege.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                      Platzhalter sind Stellvertreter in geschweiften Klammern (z. B. für Datum und
                      Uhrzeit des Webinars): Auf der Seite steht dann automatisch der aktuelle Wert —
                      auch wenn der Termin später verschoben wird. Optional.
                    </Typography>
                    <PlaceholderLegend
                      items={legendenEintraege}
                      onInsert={platzhalterEinfuegen}
                      defaultExpanded={false}
                      hint="Zuerst ins Zielfeld klicken, dann Einfügen — der Platzhalter landet an der Cursorposition. Termin und Uhrzeit ziehen sich damit automatisch aus dem Webinar, auch wenn es später verschoben wird."
                    />
                  </Box>
                )}
                <Feld
                  titel="Überschrift"
                  art="optional"
                  wofuer={
                    isDoi
                      ? 'Groß oben auf der Seite nach der Anmeldung. Leer = „Anmeldung erfolgreich“. Zeilenumbrüche im Feld erscheinen auch auf der Seite.'
                      : 'Groß oben auf der Seite. Leer = öffentlicher Titel des Webinars (aus den Webinar-Grunddaten).'
                  }
                  beispiele={{
                    feld: `lp.${pageType}.headline`,
                    webinarId,
                    onUebernehmen: (w) => setForm((prev) => ({ ...prev, headline: w })),
                  }}
                  befunde={isSignup ? pruefHeadline.befunde : undefined}
                  pruefungLaeuft={isSignup ? pruefHeadline.laeuft : undefined}
                >
                  <TextField
                    value={form.headline}
                    onChange={set('headline')}
                    onFocus={merkeFeld('headline')}
                    onBlur={() => isSignup && pruefHeadline.pruefen(form.headline, { mitKi: true })}
                    size="small"
                    fullWidth
                    inputProps={{ 'aria-label': 'Überschrift' }}
                    // Nur die Bestaetigungsseite rendert Zeilenumbrueche (lpTemplates
                    // headlineMitUmbruechen). Anderswo mehrzeilig anzubieten waere eine Zusage,
                    // die der Renderer nicht einloest.
                    multiline={isDoi}
                    minRows={isDoi ? 2 : undefined}
                  />
                </Feld>
                {isSignup && (
                  <Feld
                    titel="Unterzeile"
                    art="optional"
                    wofuer="Ein bis zwei Sätze unter der Überschrift. Leer = keine Unterzeile."
                    beispiele={{
                      feld: 'lp.signup.subheadline',
                      webinarId,
                      onUebernehmen: (w) => setForm((prev) => ({ ...prev, subheadline: w })),
                    }}
                    befunde={pruefSubheadline.befunde}
                    pruefungLaeuft={pruefSubheadline.laeuft}
                  >
                    <TextField
                      value={form.subheadline}
                      onChange={set('subheadline')}
                      onFocus={merkeFeld('subheadline')}
                      onBlur={() => pruefSubheadline.pruefen(form.subheadline, { mitKi: true })}
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      inputProps={{ 'aria-label': 'Unterzeile' }}
                    />
                  </Feld>
                )}
                <Feld
                  titel="Aufzählungspunkte"
                  art="optional"
                  wofuer="Die Nutzen-Punkte mit Häkchen — einer je Zeile. Leer = kein Aufzählungsblock."
                  beispiele={
                    isRecording
                      ? undefined
                      : {
                          feld: `lp.${pageType}.bullets`,
                          webinarId,
                          onUebernehmen: (w) => setForm((prev) => ({ ...prev, bulletsText: w })),
                        }
                  }
                  befunde={isSignup ? pruefBullets.befunde : undefined}
                  pruefungLaeuft={isSignup ? pruefBullets.laeuft : undefined}
                >
                  <TextField
                    value={form.bulletsText}
                    onChange={set('bulletsText')}
                    onFocus={merkeFeld('bulletsText')}
                    onBlur={() => isSignup && pruefBullets.pruefen(form.bulletsText, { mitKi: true })}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    inputProps={{ 'aria-label': 'Aufzählungspunkte (einer je Zeile)' }}
                  />
                </Feld>
                <Feld
                  titel="Fließtext"
                  art="optional"
                  wofuer="Längerer Text mit Absätzen, Fettung und Links. Leer = kein Fließtext."
                  befunde={isSignup ? pruefBody.befunde : undefined}
                  pruefungLaeuft={isSignup ? pruefBody.laeuft : undefined}
                >
                  <TinyMCEEditor
                    ref={bodyEditorRef}
                    onFocus={merkeEditor(bodyEditorRef)}
                    preset="lp"
                    height={260}
                    value={form.body_html}
                    onChange={(html) => setForm((prev) => ({ ...prev, body_html: html }))}
                    onNormalizedBaseline={(html) => setNormalized('body_html', html)}
                    placeholder="Absätze, Fettung, Links, Listen …"
                  />
                </Feld>
                {isSignup && (
                  <Feld
                    titel="Text auf dem Anmeldeknopf"
                    art="optional"
                    wofuer="Beschriftung des Knopfs im Anmeldeformular. Leer = „GRATIS ANMELDEN“."
                    beispiele={{
                      feld: 'lp.signup.cta_text',
                      webinarId,
                      onUebernehmen: (w) => setForm((prev) => ({ ...prev, cta_text: w })),
                    }}
                  >
                    <TextField
                      value={form.cta_text}
                      onChange={set('cta_text')}
                      onFocus={merkeFeld('cta_text')}
                      size="small"
                      fullWidth
                      inputProps={{ 'aria-label': 'Text auf dem Anmeldeknopf' }}
                    />
                  </Feld>
                )}
              </Abschnitt>

              <Abschnitt
                titel="3. Bilder und Referent"
                erklaerung="Alle Bilder sind freiwillig: leer heißt „kein Bild“ bzw. Standard-Hintergrund. Bild hineinziehen oder anklicken — es wird automatisch hochgeladen."
              >
                {/*
                  Drei Bild-Slots, seit 2026-08-19 alle OHNE Standard: leer heisst kein Bild.
                  Die Aufzeichnungsseite hat keine Emblem-Position, dort entfaellt der Slot ganz —
                  ein Knopf ohne Wirkung waere schlimmer als keiner.
                */}
                {!isRecording && (
                  <Feld
                    titel="Logo / Emblem"
                    art="optional"
                    wofuer="Kleines Logo ganz oben auf der Seite. Leer = kein Logo."
                  >
                    <BildFeld
                      uploadUrl="/api/webinar-lp/upload-image?kind=header"
                      value={form.emblem_image_url || null}
                      onChange={(url) => setForm((prev) => ({ ...prev, emblem_image_url: url }))}
                      urlHinweis="Nach dem Hochladen automatisch gesetzt — oder eine Bildadresse eintragen (z. B. ein SVG-Logo, das der Upload nicht annimmt)."
                      disabled={isSignup && form.design_variant === 'bp'}
                      disabledHint={
                        isSignup && form.design_variant === 'bp'
                          ? 'Die Variante „Best Practice“ nutzt ein fest eingebautes Finanztrends-Logo.'
                          : undefined
                      }
                    />
                  </Feld>
                )}
                {/*
                  Dasselbe Feld, aber je Seitentyp deckt es eine andere Flaeche ab — das gehoert ins
                  Label, sonst laedt jemand ein Bild hoch und sucht es an der falschen Stelle:
                  DOI legt es hinter Emblem-Streifen + Headline, die Aufzeichnung hinter die GANZE
                  Seite, die Anmeldung hinter den Hero-Block.
                */}
                <Feld
                  titel="Hintergrundbild"
                  art="optional"
                  wofuer={
                    isRecording
                      ? 'Liegt hinter der ganzen Seite. Leer = Standard-Hintergrund.'
                      : isSignup
                        ? 'Liegt hinter dem oberen Bereich mit Überschrift und Formular. Leer = Standard-Hintergrund.'
                        : 'Liegt hinter dem Kopf mit Logo und Überschrift. Leer = Standard-Hintergrund.'
                  }
                >
                  <BildFeld
                    uploadUrl="/api/webinar-lp/upload-image?kind=header"
                    value={form.hero_bg_url || null}
                    onChange={(url) => setForm((prev) => ({ ...prev, hero_bg_url: url }))}
                    urlHinweis="Nach dem Hochladen automatisch gesetzt — oder die Adresse eines Bildes (JPG/PNG/WebP) eintragen."
                  />
                </Feld>
                {/*
                  Schleier nur anbieten, wenn es ein Bild gibt — auf der Standard-Textur waere er
                  wirkungslos und die Einstellung damit eine Falle. `null` heisst „Standard des
                  Seitentyps"; sobald jemand hier etwas anfasst, steht ein ausdruecklicher Wert
                  in der Zeile und der Standard greift nicht mehr.
                */}
                {form.hero_bg_url && (
                  <Box mt={-1} mb={2}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={scrimOn}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              hero_bg_scrim: e.target.checked ? scrimDefault || 60 : 0,
                            }))
                          }
                        />
                      }
                      label="Dunkler Schleier über dem Bild"
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      Die Seite setzt weiße Schrift. Auf einem hellen Bild ist sie ohne Schleier nicht
                      lesbar — auf einem dunklen Motiv kann er weg.
                    </Typography>
                    {scrimOn && (
                      <Box px={1} pt={1} maxWidth={360}>
                        <Slider
                          value={scrimPct}
                          onChange={(_, v) =>
                            setForm((prev) => ({ ...prev, hero_bg_scrim: v as number }))
                          }
                          min={5}
                          max={100}
                          step={1}
                          marks={[
                            { value: 5, label: 'hell' },
                            { value: 100, label: 'dunkel' },
                          ]}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(v) => `${v} %`}
                          size="small"
                        />
                      </Box>
                    )}
                  </Box>
                )}
                <Feld
                  titel="Referentenbild"
                  art="optional"
                  wofuer="Foto der Person, die das Webinar hält. Leer = kein Foto."
                >
                  <BildFeld
                    uploadUrl="/api/webinar-lp/upload-image?kind=speaker"
                    value={form.speaker_image_url || null}
                    onChange={(url) => setForm((prev) => ({ ...prev, speaker_image_url: url }))}
                    urlHinweis="Nach dem Hochladen automatisch gesetzt — oder die Adresse eines Fotos (JPG/PNG/WebP) eintragen."
                  />
                </Feld>
                <Feld
                  titel="Referent (Name)"
                  art="optional"
                  wofuer="Name unter dem Foto bzw. in der Zeile „Der Experte“. Leer = kein Name."
                  befunde={referentBefunde}
                  aktion={
                    hostName && hostName !== form.speaker_name ? (
                      <Button
                        size="small"
                        onClick={() => setForm((prev) => ({ ...prev, speaker_name: hostName }))}
                        sx={{ textTransform: 'none' }}
                      >
                        Aus den Grunddaten übernehmen: {hostName}
                      </Button>
                    ) : undefined
                  }
                  beispiele={
                    isSignup
                      ? {
                          feld: 'lp.signup.speaker_name',
                          webinarId,
                          onUebernehmen: (w) => setForm((prev) => ({ ...prev, speaker_name: w })),
                        }
                      : undefined
                  }
                >
                  <TextField
                    value={form.speaker_name}
                    onChange={set('speaker_name')}
                    size="small"
                    fullWidth
                    inputProps={{ 'aria-label': 'Referent (Name)' }}
                  />
                </Feld>
              </Abschnitt>

              {isRecording && (
                <Abschnitt
                  titel="4. Video und Kaufknopf"
                  erklaerung="Die Aufzeichnung selbst und der Knopf zum Angebot, der unter dem Video erscheint."
                >
                  <Feld
                    titel="Vimeo-Video-ID"
                    art="pflicht"
                    wofuer="Die Nummer des Videos bei Vimeo, z. B. 1216417249 (nur die Zahl). Ohne sie zeigt die Seite kein Video."
                  >
                    <TextField
                      value={form.vimeo_video_id}
                      onChange={set('vimeo_video_id')}
                      size="small"
                      fullWidth
                      inputProps={{ 'aria-label': 'Vimeo-Video-ID' }}
                    />
                  </Feld>
                  <Feld
                    titel="Ziel des Kaufknopfs"
                    art="optional"
                    wofuer="Wohin der Knopf unter dem Video führt. Leer = Bestellseite des Webinars (empfohlen)."
                  >
                    <TextField
                      value={form.cta_url}
                      onChange={set('cta_url')}
                      size="small"
                      fullWidth
                      inputProps={{ 'aria-label': 'Ziel des Kaufknopfs' }}
                    />
                  </Feld>
                  <Feld
                    titel="Text auf dem Kaufknopf"
                    art="optional"
                    wofuer="Leer = „Hier klicken für alle Informationen“."
                    beispiele={{
                      feld: 'lp.recording.cta_text',
                      webinarId,
                      onUebernehmen: (w) => setForm((prev) => ({ ...prev, cta_text: w })),
                    }}
                  >
                    <TextField
                      value={form.cta_text}
                      onChange={set('cta_text')}
                      onFocus={merkeFeld('cta_text')}
                      size="small"
                      fullWidth
                      inputProps={{ 'aria-label': 'Text auf dem Kaufknopf' }}
                    />
                  </Feld>
                  <Feld
                    titel="Wann erscheint der Kaufknopf?"
                    art="optional"
                    wofuer="Beide leer = sofort sichtbar. „Sekunden nach Seitenaufruf“ zählt ab dem Öffnen der Seite, „Sekunde im Video“ ab der Stelle im Video."
                  >
                    <Box display="flex" gap={2}>
                      <TextField
                        label="Sekunden nach Seitenaufruf"
                        value={form.cta_reveal_wallclock_s}
                        onChange={set('cta_reveal_wallclock_s')}
                        size="small"
                        type="number"
                      />
                      <TextField
                        label="Sekunde im Video"
                        value={form.cta_reveal_playback_s}
                        onChange={set('cta_reveal_playback_s')}
                        size="small"
                        type="number"
                      />
                    </Box>
                    {kaufknopfVorschlag &&
                      knopfSofortImFormular(form.cta_reveal_wallclock_s, form.cta_reveal_playback_s) && (
                        <Alert
                          severity="warning"
                          sx={{ mt: 1 }}
                          data-testid="kaufknopf-hinweis"
                          action={
                            kaufknopfVorschlag.playbackSekunde !== null ? (
                              <Button
                                color="inherit"
                                size="small"
                                onClick={() =>
                                  setForm((prev) => ({
                                    ...prev,
                                    cta_reveal_wallclock_s: '',
                                    cta_reveal_playback_s: String(kaufknopfVorschlag.playbackSekunde),
                                  }))
                                }
                              >
                                Vorschlag übernehmen
                              </Button>
                            ) : undefined
                          }
                        >
                          {kaufknopfVorschlag.offerMinute !== null
                            ? `Der Knopf erscheint so sofort – im Raum kommt das Angebot erst ab Minute ${kaufknopfVorschlag.offerMinute}. Für den Test „Aufzeichnung im Raum“ gleich setzen: „Sekunde im Video“ ${kaufknopfVorschlag.playbackSekunde}, „Sekunden nach Seitenaufruf“ leer (eine 0 dort zeigt den Knopf sofort).`
                            : 'Der Knopf erscheint so sofort – im Raum kommt das Angebot erst nach dem Video. Bitte die Stelle eintragen, an der der Referent zum Angebot kommt; „Sekunden nach Seitenaufruf“ leer lassen oder größer als 0.'}
                        </Alert>
                      )}
                  </Feld>
                  <Feld
                    titel="Verlassen-Hinweis (Popup)"
                    art="optional"
                    wofuer="Erscheint frühestens nach 20 Sekunden, wenn der Besucher die Seite verlassen will. Überschrift leer = kein Popup."
                  >
                    <TextField
                      label="Popup-Überschrift"
                      value={form.exit_popup_headline}
                      onChange={set('exit_popup_headline')}
                      onFocus={merkeFeld('exit_popup_headline')}
                      size="small"
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                    <TinyMCEEditor
                      ref={popupEditorRef}
                      onFocus={merkeEditor(popupEditorRef)}
                      label="Popup-Text"
                      preset="lp"
                      height={200}
                      value={form.exit_popup_body_html}
                      onChange={(html) =>
                        setForm((prev) => ({ ...prev, exit_popup_body_html: html }))
                      }
                      onNormalizedBaseline={(html) => setNormalized('exit_popup_body_html', html)}
                    />
                  </Feld>
                </Abschnitt>
              )}

              {isSignup && (
                <Abschnitt
                  titel="4. Darstellung"
                  art="optional"
                  erklaerung="Termin, Countdown, Formular, Einwilligung und Messung kommen automatisch — hier wählst du nur das Aussehen."
                >
                  <Feld
                    titel="Design-Variante"
                    art="optional"
                    wofuer="Grundlayout der Seite. Standard ist „Dark Electric 2.0“."
                  >
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <Select
                          inputProps={{ 'aria-label': 'Design-Variante' }}
                          value={form.design_variant}
                          onChange={(e) => {
                            const next = e.target.value as FormState['design_variant'];
                            // Der neue Scope kann Felder sperren, die der alte erlaubte (bp sperrt
                            // die Schriftart). Bleiben sie im Formular stehen, sieht die Vorschau
                            // richtig aus — der Server lehnt das Speichern aber mit 400 ab. Also
                            // hier entfernen und es SAGEN; still verwerfen waere schlimmer.
                            const locked =
                              scopes?.[scopeFor(pageType, next)]?.catalog.lockedFields || [];
                            setForm((prev) => {
                              if (!locked.length) return { ...prev, design_variant: next };
                              const cleaned: TypographyTokens = { ...prev.typography };
                              let removed = 0;
                              for (const group of ['h1', 'h2', 'body', 'cta'] as const) {
                                const style = cleaned[group];
                                if (!style) continue;
                                const copy = { ...style } as Record<string, unknown>;
                                for (const field of locked) {
                                  if (copy[field] !== undefined) {
                                    delete copy[field];
                                    removed += 1;
                                  }
                                }
                                if (Object.keys(copy).length) cleaned[group] = copy;
                                else delete cleaned[group];
                              }
                              if (removed) {
                                setMessage({
                                  severity: 'info',
                                  text:
                                    `Design gewechselt: ${removed}× Schriftart entfernt — ` +
                                    (scopes?.[scopeFor(pageType, next)]?.catalog.lockedReason ||
                                      'diese Variante erlaubt das Feld nicht.'),
                                });
                              }
                              return { ...prev, design_variant: next, typography: cleaned };
                            });
                          }}
                        >
                          <MenuItem value="electric">Dark Electric 2.0 (Standard)</MenuItem>
                          <MenuItem value="dossier">Dossier (Salesletter)</MenuItem>
                          <MenuItem value="show">Show (Navy-Gold)</MenuItem>
                          <MenuItem value="bp">Best Practice (heller Test-Arm)</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.design_variant !== 'bp' && form.scarcity_timer}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, scarcity_timer: e.target.checked }))
                            }
                            size="small"
                            disabled={form.design_variant === 'bp'}
                          />
                        }
                        label={
                          form.design_variant === 'bp'
                            ? 'Verknappungs-Timer (bei Best Practice ohne Wirkung — nur echte Dringlichkeit)'
                            : 'Verknappungs-Timer'
                        }
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Verknappungs-Timer: zeigt im Verknappungs-Kasten (unten) „Ihre Reservierung
                      ist noch 05:00 Minuten für Sie geblockt“ mit ablaufender Uhr — nur wenn der
                      Kasten eine Überschrift oder einen Text hat.
                    </Typography>
                  </Feld>
                  <Feld
                    titel="Verknappungs-Block"
                    art="optional"
                    wofuer="Hervorgehobener Kasten, z. B. „Nur 500 Plätze“. Überschrift und Text leer = kein Kasten."
                  >
                    <TextField
                      label="Überschrift des Kastens"
                      value={form.exit_popup_headline}
                      onChange={set('exit_popup_headline')}
                      onFocus={merkeFeld('exit_popup_headline')}
                      size="small"
                      fullWidth
                      sx={{ mb: 1 }}
                    />
                    <TinyMCEEditor
                      ref={scarcityEditorRef}
                      onFocus={merkeEditor(scarcityEditorRef)}
                      label="Text des Kastens"
                      preset="lp"
                      height={200}
                      value={form.exit_popup_body_html}
                      onChange={(html) =>
                        setForm((prev) => ({ ...prev, exit_popup_body_html: html }))
                      }
                      onNormalizedBaseline={(html) => setNormalized('exit_popup_body_html', html)}
                    />
                  </Feld>
                </Abschnitt>
              )}

              {typo &&
                (() => {
                  // Scope der AKTUELL gewaehlten Variante, nicht der beim Laden gespeicherten.
                  // Faellt die Scope-Liste aus, bleibt der Stand der Seiten-GET — dann stimmt
                  // die Anzeige nach einem Wechsel nicht, aber der Dialog funktioniert.
                  const key = scopeFor(pageType, form.design_variant);
                  const live = scopes?.[key];
                  return (
                    <Abschnitt
                      eingeklappt
                      titel="Gestaltung (optional, für Grafik)"
                      art="optional"
                      erklaerung="Schriftgrößen, Farben und Abstände NUR dieser Seite. Nichts eintragen = zentraler Standard. Den zentralen Standard je Design-Variante (gilt für alle Seiten dieser Variante) pflegt der Reiter „Typografie-Defaults“ in der LP-Übersicht; was du hier einstellst, überschreibt ihn nur für diese Seite. Nur ändern, wenn die Grafik es verlangt."
                    >
                      <TypographyPanel
                        value={form.typography}
                        onChange={(next) => setForm((prev) => ({ ...prev, typography: next }))}
                        catalog={live?.catalog || typo.catalog}
                        defaults={live?.tokens || typo.defaults}
                        scope={live ? key : typo.scope}
                      />
                    </Abschnitt>
                  );
                })()}
            </Box>
            {/* Preview */}
            <Box flex={1} display="flex" flexDirection="column" borderLeft="1px solid #e0e0e0">
              <Box
                px={2}
                py={1}
                display="flex"
                alignItems="center"
                gap={2}
                borderBottom="1px solid #e0e0e0"
              >
                <Typography variant="subtitle2">
                  Vorschau — folgt deinen Eingaben (ins Netz gehen Änderungen erst mit „
                  {hauptKnopfText}“)
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={device}
                  onChange={(_e, v) => v && setDevice(v)}
                >
                  <ToggleButton value="desktop">Desktop</ToggleButton>
                  <ToggleButton value="mobile">Mobil 375px</ToggleButton>
                </ToggleButtonGroup>
                <Button
                  size="small"
                  component="a"
                  href={`/api/webinar-lp/pages/${webinarId}/${pageType}/preview.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  Gespeicherten Stand öffnen
                </Button>
              </Box>
              <Box
                flex={1}
                display="flex"
                justifyContent="center"
                sx={{ bgcolor: '#f0f2f5', overflow: 'auto' }}
              >
                {previewSrcDoc ? (
                  <iframe
                    title="LP-Vorschau"
                    srcDoc={previewSrcDoc}
                    sandbox="allow-same-origin"
                    style={{
                      width: device === 'mobile' ? 375 : '100%',
                      height: '100%',
                      minHeight: '68vh',
                      border: 0,
                      background: '#fff',
                    }}
                  />
                ) : (
                  <Box p={4}>
                    <CircularProgress size={22} />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2 }}>
        {status === 'published' && (
          <Button
            onClick={() => setConfirmUnpublish(true)}
            disabled={busy !== null}
            color="error"
            size="small"
          >
            {busy === 'unpublish' ? 'Nimmt vom Netz…' : 'Seite vom Netz nehmen …'}
          </Button>
        )}
        <NurAdmin>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              onClick={() => setConfirmSwitch({ old_url: null })}
              disabled={busy !== null || status !== 'published'}
              variant="outlined"
              color="warning"
              size="small"
            >
              {busy === 'switch' ? 'Schaltet…' : 'Live schalten (Admin)'}
            </Button>
            {status !== 'published' && (
              <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }}>
                Ausgegraut, weil die Seite noch nicht veröffentlicht ist — live schalten geht nur
                mit einer veröffentlichten Seite.
              </Typography>
            )}
          </Box>
        </NurAdmin>
        <Box flex={1} />
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 420, textAlign: 'right' }}>
          {hauptKnopfHinweis}
        </Typography>
        <Button onClick={versucheSchliessen}>Schließen</Button>
        <Button
          onClick={save}
          disabled={busy !== null}
          variant="outlined"
          title="Speichert deine Änderungen, ohne sie ins Netz zu stellen."
        >
          {busy === 'save' ? 'Speichert…' : 'Als Entwurf speichern'}
        </Button>
        <Button onClick={onHauptKnopf} disabled={busy !== null} variant="contained">
          {busy === 'publish'
            ? 'Veröffentlicht…'
            : busy === 'switch'
              ? 'Schaltet live…'
              : hauptKnopfText}
        </Button>
      </DialogActions>

      {/* Erstes Veröffentlichen: publizieren + aktiv schalten (F3) */}
      <GefahrBestaetigung
        open={confirmVeroeffentlichen}
        titel={`${typeLabel} veröffentlichen und aktiv schalten?`}
        knopfText="Veröffentlichen und aktiv schalten"
        onAbbrechen={() => setConfirmVeroeffentlichen(false)}
        onBestaetigen={() => void veroeffentlichenUndUmschalten()}
        wirkung={
          <>
            <Typography variant="body2" gutterBottom>
              Die Seite geht ins Netz ({pageUrl}) und wird die aktive {typeLabel} dieses
              Webinars.{' '}
              {isSignup
                ? 'Alle Werbung für dieses Webinar (Newsletter-Anzeigen, Heat-Ups, Portal-Werbung, Redlinks) führt ab sofort hierher.'
                : isRecording
                  ? 'Nachfass-Mails an Angemeldete, die nicht dabei waren, führen ab sofort hierher.'
                  : 'Wer sich anmeldet, landet ab sofort nach der Anmeldung hier.'}
            </Typography>
            {!isRecording && (
              <Typography variant="body2" gutterBottom>
                <b>Erinnerungsmails und Werbung:</b> Sind Anmelde- UND Bestätigungsseite live (und die
                übrigen Punkte unter „Bewerbbar?“ im Webinar-Fahrplan erledigt), schalten sich der
                automatische Versand der Erinnerungsmails und die Werbung für das Webinar ein. Bitte
                die Texte der Erinnerungsmails vorher prüfen (Reiter „Erinnerungsmails“ im
                Webinar).
              </Typography>
            )}
            {isSignup && confirmationGap && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {confirmationGap === 'no_campaign'
                  ? 'Achtung: Für dieses Webinar gibt es noch keine aktiven Erinnerungsmails. Wer sich ab jetzt anmeldet, bekommt WEDER Bestätigung NOCH Erinnerung — erst die Erinnerungsmails einrichten, dann veröffentlichen.'
                  : 'Achtung: Die Bestätigungsmail hat keinen Betreff oder keinen Inhalt. Wer sich ab jetzt anmeldet, bekommt keine Bestätigung.'}
              </Typography>
            )}
            {isSignup && (
              <Typography variant="body2" gutterBottom>
                Danach auch die Bestätigungsseite veröffentlichen (in der Liste Spalte „Bestätigung“
                anklicken) — beide gehören zu den Voraussetzungen, damit das Webinar beworben wird.
              </Typography>
            )}
            <Typography variant="body2">
              Rückgängig: „Seite vom Netz nehmen“ — dann zeigt das Webinar wieder auf die vorherige
              Adresse.
            </Typography>
          </>
        }
      />

      {/* Schliessen mit ungespeicherten Aenderungen */}
      <Dialog open={confirmVerwerfen} onClose={() => setConfirmVerwerfen(false)}>
        <DialogTitle>Ungespeicherte Änderungen verwerfen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Du hast Änderungen{uebernommenRef.current ? ' (auch die Übernahme aus einem anderen Webinar)' : ''}{' '}
            noch nicht gespeichert. Beim Schließen gehen sie verloren.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmVerwerfen(false)}>Weiter bearbeiten</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setConfirmVerwerfen(false);
              onClose();
            }}
          >
            Verwerfen und schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unpublish confirmation */}
      <GefahrBestaetigung
        open={confirmUnpublish}
        titel="Seite vom Netz nehmen?"
        knopfText="Vom Netz nehmen"
        bestaetigungsWort={webinarId}
        onAbbrechen={() => setConfirmUnpublish(false)}
        onBestaetigen={() => void doUnpublish()}
        wirkung={
          <>
            <Typography variant="body2" gutterBottom>
              Die Seite unter {pageUrl} ist danach nicht mehr erreichbar
              {isSignup ? ' (auch die Test-Variante B)' : ''}. Der Inhalt bleibt gespeichert und kann
              erneut veröffentlicht werden.
            </Typography>
            <Typography variant="body2">
              {isSignup
                ? 'War sie die aktive Anmeldeseite, zeigt das Webinar wieder auf die vorherige Anmeldeadresse — und gilt dann NICHT mehr als bewerbbar: Anzeigen, Redlinks und Heat-Ups stoppen.'
                : isRecording
                  ? 'War sie die aktive Aufzeichnungsseite, wird die Adresse im Webinar geleert — Links in bereits verschickten Nachfass-Mails führen dann ins Leere.'
                  : 'War sie die aktive Bestätigungsseite, wird die Adresse im Webinar geleert — das Webinar ist dann nicht mehr bewerbbar, Links in verschickten Bestätigungsmails führen ins Leere.'}
            </Typography>
          </>
        }
      />

      {/* Switch confirmation */}
      <Dialog open={confirmSwitch !== null} onClose={() => setConfirmSwitch(null)}>
        <DialogTitle>Live schalten?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Das setzt {isSignup ? 'signup_url' : isRecording ? 'missed_webinar_url' : 'doi_url'} des
            Webinars auf die lp.finanztrends.de-Seite. Ab sofort landen{' '}
            {isSignup
              ? 'alle Mail-/Ad-Klicks'
              : isRecording
                ? 'Nachfass-Empfänger'
                : 'neue Anmeldungen'}{' '}
            dort.
            {isSignup
              ? ' Die alte PL-X-URL wird historisiert (Auswertungen bleiben intakt); Rückgängig: erneuter Switch rückwärts im Webinar-Management.'
              : ' Rückgängig: alte URL im Webinar-Management wieder eintragen — die PL-X-Seite bleibt online.'}
          </Typography>
          {isSignup && confirmationGap && (
            /* Der Reihenfolge-Fehler vom 22.08.2026: Anmeldeseite 14:18 live, Kampagne erst
               23:48 — dazwischen bekam eine echte Anmeldung nichts. Die Warnung steht bewusst
               HIER und nicht in der Erfolgsmeldung danach: dort waere sie zu spaet und stuende
               ausserdem in einer gruenen Meldung. */
            <Alert severity="warning" sx={{ mt: 2 }}>
              {confirmationGap === 'no_campaign'
                ? 'Keine aktive Reminder-Kampagne für dieses Webinar. Anmelder bekommen ab dem Umschalten WEDER Bestätigung NOCH Reminder — erst die Kampagne scharf schalten, dann umschalten.'
                : 'Die Reminder-Kampagne ist aktiv, aber die Bestätigungsmail hat keinen Betreff oder keinen Inhalt. Anmelder bekommen nichts.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmSwitch(null)}>Abbrechen</Button>
          <Button onClick={() => void doSwitch()} variant="contained" color="warning">
            Ja, live schalten
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vorlagen-Uebernahme: Rueckfrage, wenn das Ziel schon Inhalt traegt */}
      <Dialog open={confirmTemplate !== null} onClose={() => setConfirmTemplate(null)}>
        <DialogTitle>Inhalt überschreiben?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Überschrift, Texte, Bilder, Verlassen-Hinweis und Gestaltung dieser Seite werden durch die von „
            {confirmTemplate?.title}“ ersetzt.{' '}
            {isRecording && (
              <>
                <strong>Kaufknopf-Ziel, Video und die beiden Einblendzeiten werden dabei geleert</strong>{' '}
                — sie gehören zum Quell-Webinar und dürfen nicht mitwandern.
              </>
            )}
            <br />
            Gespeichert wird nichts: bis zum Klick auf „Als Entwurf speichern“ oder „Veröffentlichen“ bleibt die gespeicherte Fassung
            unberührt, Schließen des Dialogs verwirft die Übernahme.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTemplate(null)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={() => {
              const src = confirmTemplate;
              setConfirmTemplate(null);
              if (src) applyTemplate(src);
            }}
          >
            Ja, übernehmen
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default LpPageEditorDialog;
